import type { InputHTMLAttributes, ReactNode, SVGProps, TextareaHTMLAttributes } from "react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangleIcon, CheckIcon } from "../icons";

/**
 * The Register flow's form chrome — her `.card` / `.f` / `.seg` / `.chip` /
 * `.sw` / `.note` (app.css) at her sizes, in rem: 10px cards padded 16 / 20,
 * 13.5px medium labels, 48px fields at 15.5px, 12.5px hints. Translation
 * comes from the caller; these only lay text out.
 */

type P = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;
function Stroke({ d, ...props }: P & { d: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="none" aria-hidden focusable={false} {...props}>
			<path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}
export const SaveIcon = (p: P) => <Stroke {...p} d="M5 4h11l3 3v13H5zM8 4v5h7V4M8 20v-6h8v6" />;
export const ChevronLeftIcon = (p: P) => <Stroke {...p} d="M15 6l-6 6 6 6" />;
export const ChevronRightIcon = (p: P) => <Stroke {...p} d="M9 6l6 6-6 6" />;
export const ShieldIcon = (p: P) => <Stroke {...p} d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6l-8-3zM9 12l2 2 4-4" />;
export const IdCardIcon = (p: P) => <Stroke {...p} d="M3 6h18v12H3zM7 10h4M7 14h6M15.5 11.5h2" />;
export const PassportIcon = (p: P) => <Stroke {...p} d="M6 3h12v18H6zM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM9 17h6" />;
export const ListIcon = (p: P) => <Stroke {...p} d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />;
export const InfoIcon = (p: P) => <Stroke {...p} d="M12 11v5M12 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />;

export const inputClass = (invalid: boolean, extra = "") =>
	`input h-[3rem] w-full border-gms-line-2 bg-base-100 focus:border-gms-navy focus:outline-none placeholder:text-[#9a9cb2] ${invalid ? "input-error" : ""} ${extra}`;

/** Her step card: a title, a "Done" / "N to fill" / "Optional" badge on the right, then the fields. */
export function StepCard({ title, badge, children, id }: { title: ReactNode; badge?: ReactNode; children: ReactNode; id?: string }) {
	const hid = useId();
	return (
		<section id={id} aria-labelledby={hid} className="rounded-box border border-gms-line bg-base-100 p-[1rem] min-[45rem]:p-[1.25rem]">
			<header className="mb-[0.875rem] flex items-center justify-between gap-[0.625rem]">
				<h3 id={hid} className="text-[0.96875rem] leading-[1.3] font-semibold text-gms-navy-ink">
					{title}
				</h3>
				{badge}
			</header>
			<div className="grid gap-[0.875rem]">{children}</div>
		</section>
	);
}

/** Her `.cbadge`: "Done" (green, ticked) / "2 to fill" (grey) / "Optional" (quiet text). */
export function CardBadge({ left, optional = false }: { left: number; optional?: boolean }) {
	const { t } = useTranslation("accreditation");
	if (optional && left === 0)
		return <span className="text-[0.6875rem] font-semibold whitespace-nowrap text-gms-ink-3">{t("register.badge.optional")}</span>;
	if (left === 0)
		return (
			<span className="inline-flex h-[1.375rem] items-center gap-[0.25rem] rounded-selector bg-gms-ok-soft px-[0.5rem] text-[0.6875rem] font-semibold whitespace-nowrap text-gms-ok">
				<CheckIcon className="size-[0.8125rem]" />
				{t("register.badge.done")}
			</span>
		);
	return (
		<span className="inline-flex h-[1.375rem] items-center rounded-selector bg-gms-soft-2 px-[0.5rem] text-[0.6875rem] font-semibold whitespace-nowrap text-gms-ink-2">
			{t("register.badge.toFill", { count: left })}
		</span>
	);
}

/** Label · control · hint / warning / error. `htmlFor` ties the label; `id` of the error is `${htmlFor}-error`. */
export function F({
	label,
	htmlFor,
	required,
	hint,
	warning,
	error,
	aside,
	children,
	className = "",
}: {
	label: ReactNode;
	htmlFor?: string;
	required?: boolean;
	hint?: ReactNode;
	warning?: ReactNode;
	error?: string;
	aside?: ReactNode;
	children: ReactNode;
	className?: string;
}) {
	return (
		<div className={`flex min-w-0 flex-col gap-[0.375rem] ${className}`}>
			<div className="flex items-center justify-between gap-[0.5rem]">
				<label htmlFor={htmlFor} className="inline-flex items-center gap-[0.375rem] text-[0.84375rem] leading-[1.3] font-medium text-gms-navy-ink">
					<span>{label}</span>
					{required && (
						<span aria-hidden className="text-gms-crit">
							*
						</span>
					)}
				</label>
				{aside && <span className="text-[0.75rem] leading-none text-gms-ink-3 tabular-nums">{aside}</span>}
			</div>
			{children}
			{error ? (
				<p id={htmlFor ? `${htmlFor}-error` : undefined} role="alert" className="flex items-start gap-[0.3125rem] text-[0.78125rem] leading-[1.4] font-medium text-gms-bad">
					<AlertTriangleIcon className="mt-[0.0625rem] size-[0.9375rem] shrink-0" />
					{error}
				</p>
			) : warning ? (
				<p className="flex items-start gap-[0.3125rem] text-[0.78125rem] leading-[1.4] font-medium text-gms-warn">
					<AlertTriangleIcon className="mt-[0.0625rem] size-[0.9375rem] shrink-0" />
					{warning}
				</p>
			) : hint ? (
				<p className="text-[0.78125rem] leading-[1.4] text-gms-ink-3">{hint}</p>
			) : null}
		</div>
	);
}

export function TextInput({ invalid, className = "", ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
	return <input {...rest} aria-invalid={invalid || undefined} aria-describedby={invalid && rest.id ? `${rest.id}-error` : undefined} className={inputClass(!!invalid, className)} />;
}

export function TextArea({ invalid, className = "", ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
	return (
		<textarea
			{...rest}
			aria-invalid={invalid || undefined}
			className={`textarea min-h-[5.25rem] w-full border-gms-line-2 bg-base-100 px-[0.875rem] py-[0.75rem] leading-[1.45] focus:border-gms-navy focus:outline-none placeholder:text-[#9a9cb2] ${invalid ? "textarea-error" : ""} ${className}`}
		/>
	);
}

/** Her `.seg`: two or three options, one picked (Sex, wheelchair type). */
export function Segmented<V extends string>({
	value,
	onChange,
	options,
	label,
	invalid,
	id,
}: {
	value: V | "";
	onChange: (v: V) => void;
	options: ReadonlyArray<{ value: V; label: string }>;
	label: string;
	invalid?: boolean;
	id?: string;
}) {
	return (
		<div
			id={id}
			role="radiogroup"
			aria-label={label}
			className={`grid auto-cols-fr grid-flow-col gap-[0.25rem] rounded-field bg-gms-soft-2 p-[0.25rem] ${invalid ? "shadow-[0_0_0_1px_var(--color-gms-bad)]" : ""}`}
		>
			{options.map((o) => {
				const on = o.value === value;
				return (
					<button
						key={o.value}
						type="button"
						role="radio"
						aria-checked={on}
						onClick={() => onChange(o.value)}
						className={`min-h-[2.5rem] rounded-[0.375rem] px-[0.625rem] text-[0.875rem] transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gms-navy ${
							on
								? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]"
								: "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
						}`}
					>
						{o.label}
					</button>
				);
			})}
		</div>
	);
}

/** Her `.chips`: toggle buttons (meal requirements, shirt size). */
export function ChipGroup<V extends string>({
	values,
	onToggle,
	options,
	label,
	id,
}: {
	values: readonly string[];
	onToggle: (v: V) => void;
	options: ReadonlyArray<{ value: V; label: string }>;
	label: string;
	id?: string;
}) {
	return (
		<div id={id} role="group" aria-label={label} className="flex flex-wrap gap-[0.5rem]">
			{options.map((o) => {
				const on = values.includes(o.value);
				return (
					<button
						key={o.value}
						type="button"
						aria-pressed={on}
						onClick={() => onToggle(o.value)}
						className={`inline-flex h-[2.25rem] items-center gap-[0.375rem] rounded-field border px-[0.875rem] text-[0.84375rem] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gms-navy ${
							on ? "border-gms-navy bg-gms-navy text-white" : "border-gms-line-2 bg-base-100 text-gms-ink-2 hover:border-gms-navy"
						}`}
					>
						{on && <CheckIcon className="size-[0.875rem]" />}
						{o.label}
					</button>
				);
			})}
		</div>
	);
}

/** Her `.sw`: a labelled switch row — 48px tall, a 44×26 track that turns navy. */
export function SwitchRow({ label, hint, checked, onChange, id }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void; id?: string }) {
	const auto = useId();
	const cid = id ?? auto;
	return (
		<label htmlFor={cid} className="flex min-h-[3rem] cursor-pointer items-center justify-between gap-[0.875rem]">
			<span className="grid">
				<span className="text-[0.90625rem] font-medium text-gms-navy-ink">{label}</span>
				{hint && <span className="text-[0.78125rem] text-gms-ink-3">{hint}</span>}
			</span>
			<input id={cid} type="checkbox" role="switch" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
			<span
				aria-hidden
				className="relative h-[1.625rem] w-[2.75rem] shrink-0 rounded-full bg-gms-line-2 transition-colors peer-checked:bg-gms-navy peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-gms-navy after:absolute after:top-[0.1875rem] after:left-[0.1875rem] after:size-[1.25rem] after:rounded-full after:bg-white after:shadow-[0_1px_2px_rgba(0,0,0,0.2)] after:transition-transform peer-checked:after:translate-x-[1.125rem]"
			/>
		</label>
	);
}

/** Two fields side by side from the small breakpoint up. */
export function Pair({ children }: { children: ReactNode }) {
	return <div className="grid gap-[0.875rem] min-[45rem]:grid-cols-2 min-[45rem]:gap-[0.75rem]">{children}</div>;
}

/** Her `.note`: a calm box for a fact the user should read before continuing. */
export function Note({ tone = "info", title, children, action }: { tone?: "info" | "warn" | "bad" | "ok"; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
	const toneClass = tone === "warn" ? "bg-gms-warn-soft" : tone === "bad" ? "bg-gms-bad-soft" : tone === "ok" ? "bg-gms-ok-soft" : "bg-gms-navy-soft";
	const iconClass = tone === "warn" ? "text-gms-warn" : tone === "bad" ? "text-gms-bad" : tone === "ok" ? "text-gms-ok" : "text-gms-navy";
	const Icon = tone === "info" ? InfoIcon : tone === "ok" ? CheckIcon : AlertTriangleIcon;
	return (
		<div role={tone === "bad" ? "alert" : undefined} className={`flex flex-wrap items-start gap-x-[0.625rem] gap-y-[0.5rem] rounded-field px-[0.875rem] py-[0.75rem] text-[0.8125rem] leading-[1.45] text-gms-navy-ink ${toneClass}`}>
			<Icon className={`mt-[0.0625rem] size-[1.125rem] shrink-0 ${iconClass}`} />
			<div className="grid min-w-0 flex-[1_1_14rem] gap-[0.125rem]">
				{title && <b className={`font-semibold ${tone === "bad" ? "text-gms-bad" : ""}`}>{title}</b>}
				{children && <div className="text-gms-navy-ink/85">{children}</div>}
			</div>
			{action}
		</div>
	);
}
